<template>
    <v-row class="ma-0 pb-offset">

        <!-- Header -->
        <v-col cols="12" class="pa-0 mb-4">
            <v-card class="round-card background-2">
                <v-card-title class="pa-3">
                    <v-icon color="primary" class="mr-3">mdi-shield-check-outline</v-icon>
                    <span>Politiche SLA</span>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        fab
                        small
                        @click="openCreate"
                        :disabled="loading"
                    >
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-card-title>
            </v-card>
        </v-col>

        <!-- Loading -->
        <v-col v-if="loading" cols="12" class="pa-0 text-center py-8">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        </v-col>

        <!-- Empty state -->
        <v-col v-else-if="slas.length === 0" cols="12" class="pa-0">
            <v-alert type="info" text class="mb-4">
                <div class="font-weight-medium">Nessuna politica SLA configurata</div>
                <div class="caption mt-1">Clicca su "+" per aggiungere una nuova politica SLA.</div>
            </v-alert>
        </v-col>

        <!-- SLA cards -->
        <template v-else>
            <v-col
                v-for="sla in slas"
                :key="sla.id"
                cols="12"
                class="pa-0 mb-3"
            >
                <v-card class="round-card background-2">
                    <v-card-title class="pa-3 pb-1">
                        <v-icon color="primary" class="mr-2">mdi-gauge</v-icon>
                        <span class="subtitle-1 font-weight-medium">{{ sla.name }}</span>
                        <v-chip
                            v-if="sla.is_default"
                            x-small
                            color="primary"
                            class="ml-2"
                        >
                            Default
                        </v-chip>
                        <v-spacer></v-spacer>
                        <v-btn icon small @click="openEdit(sla)">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small @click="duplicate(sla)">
                            <v-icon small>mdi-content-duplicate</v-icon>
                        </v-btn>
                        <v-btn icon small color="error" @click="confirmDelete(sla)">
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-3 pt-1">
                        <span class="caption grey--text" v-if="sla.working_hours_config_name">
                            <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                            {{ sla.working_hours_config_name }}
                        </span>
                        <span class="caption grey--text" v-else-if="sla.working_hours_config">
                            <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                            {{ sla.working_hours_config.name }}
                        </span>
                    </v-card-text>
                </v-card>
            </v-col>
        </template>

        <!-- Create / Edit dialog -->
        <v-dialog v-model="dialog" max-width="700" persistent>
            <v-card class="round-card">
                <v-card-title class="pa-4">
                    <v-icon color="primary" class="mr-2">mdi-shield-check-outline</v-icon>
                    {{ editingId ? 'Modifica Politica SLA' : 'Nuova Politica SLA' }}
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text class="pa-4" v-if="!loadingForm">
                    <sla-config-editor
                        ref="slaEditor"
                        v-model="form"
                        :priorities="formOptions.priorities"
                        :request-types="formOptions.request_types"
                        :working-hours-configs="formOptions.working_hours_configs"
                    ></sla-config-editor>
                </v-card-text>
                <v-card-text v-else class="pa-8 text-center">
                    <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions class="pa-3">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeDialog" :disabled="saving">Annulla</v-btn>
                    <v-btn color="primary" @click="save" :loading="saving">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Delete confirmation -->
        <v-dialog v-model="deleteDialog" max-width="400">
            <v-card class="round-card">
                <v-card-title>
                    <v-icon color="error" class="mr-2">mdi-alert</v-icon>
                    Conferma Eliminazione
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    Sei sicuro di voler eliminare la politica SLA
                    <strong>{{ slaToDelete ? slaToDelete.name : '' }}</strong>?
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">Annulla</v-btn>
                    <v-btn color="error" @click="deleteSla" :loading="deleting">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-row>
</template>

<script>
import SlaConfigEditor from './SlaConfigEditor.vue'

function emptyForm() {
    return {
        name: '',
        working_hours_config_id: null,
        is_default: false,
        targets: []
    }
}

export default {
    name: 'SlaConfigList',

    components: {
        SlaConfigEditor
    },

    data() {
        return {
            loading: false,
            slas: [],
            dialog: false,
            loadingForm: false,
            editingId: null,
            form: emptyForm(),
            formOptions: {
                priorities: [],
                request_types: [],
                working_hours_configs: []
            },
            saving: false,
            deleteDialog: false,
            slaToDelete: null,
            deleting: false
        }
    },

    methods: {
        loadSlas() {
            this.loading = true
            this.axios.post('helpdeskslas-filters', {})
                .then(response => {
                    this.slas = response.data.data || response.data || []
                })
                .catch(error => {
                    console.error('Error loading SLAs:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Errore nel caricamento delle politiche SLA',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },

        openCreate() {
            this.editingId = null
            this.form = emptyForm()
            this.loadingForm = true
            this.dialog = true
            this.axios.get('helpdeskslas/create')
                .then(response => {
                    const data = response.data.data || response.data
                    this.formOptions = {
                        priorities: data.priorities || [],
                        request_types: data.request_types || [],
                        working_hours_configs: data.working_hours_configs || []
                    }
                })
                .catch(error => {
                    console.error('Error loading SLA create form:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Errore nel caricamento del modulo',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.loadingForm = false
                })
        },

        openEdit(sla) {
            this.editingId = sla.id
            this.loadingForm = true
            this.dialog = true
            this.axios.get(`helpdeskslas/${sla.id}/edit`)
                .then(response => {
                    const data = response.data.data || response.data
                    this.formOptions = {
                        priorities: data.priorities || [],
                        request_types: data.request_types || [],
                        working_hours_configs: data.working_hours_configs || []
                    }
                    const model = data.model || data.sla
                    this.form = {
                        name: model ? model.name : (sla.name || ''),
                        working_hours_config_id: model ? model.working_hours_config_id : (sla.working_hours_config_id || null),
                        is_default: model ? !!model.is_default : !!sla.is_default,
                        targets: model && model.targets ? JSON.parse(JSON.stringify(model.targets)) : []
                    }
                })
                .catch(error => {
                    console.error('Error loading SLA edit form:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Errore nel caricamento della politica SLA',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.loadingForm = false
                })
        },

        duplicate(sla) {
            this.editingId = null
            this.loadingForm = true
            this.dialog = true
            this.axios.get(`helpdeskslas/${sla.id}/edit`)
                .then(response => {
                    const data = response.data.data || response.data
                    this.formOptions = {
                        priorities: data.priorities || [],
                        request_types: data.request_types || [],
                        working_hours_configs: data.working_hours_configs || []
                    }
                    const model = data.model || data.sla
                    this.form = {
                        name: (model ? model.name : sla.name) + ' (copia)',
                        working_hours_config_id: model ? model.working_hours_config_id : (sla.working_hours_config_id || null),
                        is_default: false,
                        targets: model && model.targets ? JSON.parse(JSON.stringify(model.targets)) : []
                    }
                })
                .catch(error => {
                    console.error('Error duplicating SLA:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Errore nella duplicazione',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.loadingForm = false
                })
        },

        closeDialog() {
            this.dialog = false
            this.editingId = null
            this.form = emptyForm()
        },

        save() {
            if (!this.form.name) {
                this.$store.commit('setSnackbar', {
                    value: true,
                    text: 'Il nome è obbligatorio',
                    color: 'warning',
                    timeout: 3000
                })
                return
            }
            this.saving = true

            // Retrieve full targets array from the editor component (covers all priority × request_type combos)
            const targets = this.$refs.slaEditor
                ? this.$refs.slaEditor.toApiTargets()
                : this.form.targets

            const payload = {
                name: this.form.name,
                working_hours_config_id: this.form.working_hours_config_id,
                is_default: this.form.is_default,
                targets
            }
            const request = this.editingId
                ? this.axios.put(`helpdeskslas/${this.editingId}`, payload)
                : this.axios.post('helpdeskslas', payload)

            request
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: this.editingId ? 'Politica SLA aggiornata con successo' : 'Politica SLA creata con successo',
                        color: 'success',
                        timeout: 3000
                    })
                    this.closeDialog()
                    this.loadSlas()
                })
                .catch(error => {
                    console.error('Error saving SLA:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore nel salvataggio',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.saving = false
                })
        },

        confirmDelete(sla) {
            this.slaToDelete = sla
            this.deleteDialog = true
        },

        deleteSla() {
            if (!this.slaToDelete) return
            this.deleting = true
            this.axios.post('helpdeskslas/delete', { ids: [this.slaToDelete.id] })
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Politica SLA eliminata con successo',
                        color: 'success',
                        timeout: 3000
                    })
                    this.deleteDialog = false
                    this.slaToDelete = null
                    this.loadSlas()
                })
                .catch(error => {
                    console.error('Error deleting SLA:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore nell\'eliminazione',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.deleting = false
                })
        }
    },

    mounted() {
        this.loadSlas()
    }
}
</script>
